import type {
  Habitat,
  LandmarkCardDef,
  LandmarkEffect,
  Wildlife,
  WildlifeRule,
  WildlifeScoringCardDef,
} from "@cloudnest/cascadia-engine";

/**
 * Cascadia placeholder scoring/landmark card — board-game-client copy.
 *
 * Originally derived from the (removed) board-game-playground for visual parity. UI is NOT shared (CLAUDE.md).
 * Renders card text directly from the rule schema — never from a bitmap.
 */
export type PlaceholderCardProps =
  | {
      readonly kind: "wildlife";
      readonly card: WildlifeScoringCardDef;
      readonly width?: number;
      readonly testId?: string;
    }
  | {
      readonly kind: "landmark";
      readonly card: LandmarkCardDef;
      readonly width?: number;
      readonly testId?: string;
    };

const SPECIES_LABEL: Record<Wildlife, string> = {
  bear: "Bear",
  elk: "Elk",
  salmon: "Salmon",
  hawk: "Hawk",
  fox: "Fox",
};

const HABITAT_LABEL: Record<Habitat, string> = {
  forest: "Forest",
  mountain: "Mountain",
  prairie: "Prairie",
  river: "River",
  wetland: "Wetland",
};

function summariseWildlifeRule(rule: WildlifeRule): readonly string[] {
  switch (rule.kind) {
    case "pointsPerGroupOfSize":
      return [
        `Group of size ${rule.size} (${rule.mode}) → ${rule.points}p${rule.cap !== undefined ? `, cap ${rule.cap}` : ""}`,
      ];
    case "pointsPerLine": {
      const entries = Object.entries(rule.pointsPerStraight)
        .map(([len, pts]) => `len ${len} → ${pts}p`)
        .join(", ");
      return [`Lines (min length ${rule.minLength})`, entries];
    }
    case "pointsPerAdjacentDistinctSpeciesCount": {
      const entries = Object.entries(rule.counts)
        .map(([n, pts]) => `${n} distinct → ${pts}p`)
        .join(", ");
      return [`Adjacent distinct: ${entries}`];
    }
    case "pointsPerSurroundingTileCount": {
      const entries = Object.entries(rule.counts)
        .map(([n, pts]) => `${n} neighbours → ${pts}p`)
        .join(", ");
      return [`Surrounding tiles: ${entries}`];
    }
    case "pointsPerPairWithinDistance":
      return [
        `Pairs within ${rule.distance} → ${rule.pointsPerPair}p${rule.cap !== undefined ? `, cap ${rule.cap}` : ""}`,
      ];
    case "ksCustom":
      return [`KS Promo rule: ${rule.promoId}`];
    default: {
      const _exhaustive: never = rule;
      return [_exhaustive];
    }
  }
}

function summariseLandmarkEffect(effect: LandmarkEffect): readonly string[] {
  switch (effect.kind) {
    case "flatBonus":
      return [`+${effect.points}p flat bonus`];
    case "addToCorridor":
      return [
        `+${effect.pointsPerTileInLargest}p per tile in largest ${HABITAT_LABEL[effect.habitat]} corridor`,
      ];
    case "addToMajority":
      return [`+${effect.bonus}p to ${HABITAT_LABEL[effect.habitat]} majority`];
    case "singleTileCorridorCounts":
      return [`Single-tile ${HABITAT_LABEL[effect.habitat]} corridors count`];
    case "minTwoTileCorridor":
      return [`${HABITAT_LABEL[effect.habitat]} corridors require ≥ 2 tiles`];
    case "keystoneCountsAsHabitat":
      return [
        `Keystones count as ${HABITAT_LABEL[effect.habitat]} for scoring`,
      ];
    case "cappedBonus":
      return [
        `${effect.perTile}p per ${HABITAT_LABEL[effect.habitat]} tile, cap ${effect.capPoints}, floor ${effect.floor}`,
      ];
    case "pointsPerAnimalOnHabitat":
      return [
        `+${effect.pointsPerAnimal}p per ${SPECIES_LABEL[effect.wildlife]} on ${HABITAT_LABEL[effect.habitat]}`,
      ];
    case "ksCustom":
      return [`KS Promo effect: ${effect.promoId}`];
    default: {
      const _exhaustive: never = effect;
      return [_exhaustive];
    }
  }
}

export function PlaceholderCard(props: PlaceholderCardProps) {
  const width = props.width ?? 240;
  const height = width * 1.4;

  if (props.kind === "wildlife") {
    const { card } = props;
    const lines = summariseWildlifeRule(card.rule);
    return (
      <g data-testid={props.testId ?? `placeholder-card-${card.cardId}`}>
        <rect
          width={width}
          height={height}
          rx={width * 0.05}
          fill="var(--cn-cascadia-bg)"
          stroke="var(--cn-cascadia-tile-outline)"
          strokeWidth={1}
        />
        <text
          x={width / 2}
          y={width * 0.15}
          textAnchor="middle"
          fontSize={width * 0.09}
          fontWeight={700}
          fill="var(--cn-cascadia-label)"
        >
          {SPECIES_LABEL[card.wildlife]} · {card.variantId}
        </text>
        {lines.map((line, i) => (
          <text
            key={`rule-${i}`}
            x={width * 0.08}
            y={width * 0.32 + i * width * 0.12}
            fontSize={width * 0.07}
            fill="var(--cn-cascadia-label)"
          >
            {line}
          </text>
        ))}
      </g>
    );
  }

  const { card } = props;
  const lines = summariseLandmarkEffect(card.effect);
  return (
    <g data-testid={props.testId ?? `placeholder-card-${card.cardId}`}>
      <rect
        width={width}
        height={height}
        rx={width * 0.05}
        fill="var(--cn-cascadia-bg)"
        stroke="var(--cn-cascadia-landmark-bronze)"
        strokeWidth={2}
      />
      <text
        x={width / 2}
        y={width * 0.15}
        textAnchor="middle"
        fontSize={width * 0.09}
        fontWeight={700}
        fill="var(--cn-cascadia-label)"
      >
        {HABITAT_LABEL[card.habitat]} · {card.variantId}
      </text>
      {lines.map((line, i) => (
        <text
          key={`effect-${i}`}
          x={width * 0.08}
          y={width * 0.32 + i * width * 0.12}
          fontSize={width * 0.07}
          fill="var(--cn-cascadia-label)"
        >
          {line}
        </text>
      ))}
    </g>
  );
}
